import { createContext, Dispatch, FC, SetStateAction, useCallback, useContext, useEffect, useState } from 'react'
import { CreateReviewActionConfig } from 'stores/Workflows/Types'
import { ModalPromptProps } from '../../hooks/useActionModalPrompt'
import { observer } from 'mobx-react-lite'
import { useRootStore } from '../../stores/RootStore'
import { ReviewerRole, ReviewPrivacy, ReviewType } from '../ReviewsWizard/types'
import { dehydrateValues, getParticipantsBuckets, parseReviewers, Reviewer } from './utils'
import dayjs from 'dayjs'
import { Value } from 'components/ShareSheet/RecipientSelector/Types'
import { ReviewConfigInput, useReviewValidator } from './validation'
import { ValidationError } from 'yup'
import { ArticleFlat } from '../../types'
import { reviewTitle } from '../ReviewsWizard/reviewTitle'
import { ourUuid } from '../../utils'
import { useModals } from 'almanac-ui/Modal'

type AdditionalPayload = {
  add_members: boolean
  email_reviewers: string[]
  email_approvers: string[]
  email_viewers: string[]
}

type ReviewConfigProps = ModalPromptProps<
  Partial<CreateReviewActionConfig & AdditionalPayload>,
  {
    article?: ArticleFlat
    reviewType: ReviewType
  }
>

interface ReviewConfigProviderValue {
  reviewers: Reviewer[]
  viewers: Reviewer[]
  approvers: Reviewer[]
  reviewerRoles: ReviewerRole[]
  setReviewerRoles: Dispatch<SetStateAction<ReviewerRole[]>>
  setReviewerValues: (values: Value[]) => any
  setApproverValues: (values: Value[]) => any
  setViewerValues: (values: Value[]) => any
  htmlMessage?: string
  setHtmlMessage: Dispatch<SetStateAction<string | undefined>>
  endsOn: string
  setEndsOn: Dispatch<SetStateAction<string>>
  onSubmit: (addMembers: boolean) => void
  errors: string[]
  title?: string
  privacy: ReviewPrivacy
  setPrivacy: Dispatch<SetStateAction<ReviewPrivacy>>
  canAddReviewers: boolean
  canChangePrivacy: boolean
}

const ReviewConfigContext = createContext<ReviewConfigProviderValue | undefined>(undefined)

export const ReviewConfigProvider: FC<React.PropsWithChildren<ReviewConfigProps>> = observer(
  ({
    children,
    reviewers: defaultReviewers = [],
    approvers: defaultApprovers = [],
    viewers: defaultViewers = [],
    channel_reviewers: defaultChannelReviewers = [],
    channel_approvers: defaultChannelApprovers = [],
    channel_viewers: defaultChannelViewers = [],
    reviewer_roles: defaultRoles = [ReviewerRole.REVIEWER],
    onConfirm,
    html_description,
    duration,
    article,
    reviewType,
    title: titleProp,
    privacy: configPrivacy,
  }) => {
    const rootStore = useRootStore()
    const validate = useReviewValidator(reviewType)
    const { closeModal } = useModals()
    let newTitle = titleProp
    if (newTitle === undefined && article) {
      newTitle = article.title
    }
    const docStore = rootStore.docStore
    const canAddReviewers = reviewType !== ReviewType.READ_RECEIPTS || !docStore?.isVersionControlled
    const canChangePrivacy = reviewType === ReviewType.READ_RECEIPTS || !docStore?.isVersionControlled
    const title = newTitle ? reviewTitle(newTitle, reviewType) : undefined

    useEffect(() => {
      // hydrate the store
      ;[...defaultReviewers, ...defaultApprovers, ...defaultViewers].forEach(rootStore.users.fetch)
      ;[...defaultChannelReviewers, ...defaultChannelApprovers, ...defaultChannelViewers].forEach(
        rootStore.channels.fetch,
      )
    }, [rootStore]) // eslint-disable-line react-hooks/exhaustive-deps

    const [reviewerRoles, setReviewerRoles] = useState(() => {
      const roles = new Set(defaultRoles)
      if (defaultReviewers.length > 0 && canAddReviewers) {
        roles.add(ReviewerRole.REVIEWER)
      }
      if (defaultViewers.length > 0) {
        roles.add(ReviewerRole.VIEWER)
      }
      if (defaultApprovers.length > 0 && canAddReviewers) {
        roles.add(ReviewerRole.APPROVER)
      }
      return Array.from(roles)
    })

    const [reviewers, setReviewers] = useState<Reviewer[]>(() =>
      canAddReviewers ? parseReviewers(defaultReviewers, defaultChannelReviewers) : [],
    )
    const [approvers, setApprovers] = useState<Reviewer[]>(() =>
      canAddReviewers ? parseReviewers(defaultApprovers, defaultChannelApprovers) : [],
    )
    const [viewers, setViewers] = useState<Reviewer[]>(() => parseReviewers(defaultViewers, defaultChannelViewers))

    const [htmlMessage, setHtmlMessage] = useState(html_description)
    const [endsOn, setEndsOn] = useState(() =>
      dayjs()
        .add(duration || 7, 'day')
        .format('YYYY-MM-DD'),
    )
    const [errors, setErrors] = useState<string[]>([])
    const defaultPrivacy = configPrivacy || ReviewPrivacy.PUBLIC
    const [privacy, setPrivacy] = useState<ReviewPrivacy>(!canChangePrivacy ? ReviewPrivacy.PRIVATE : defaultPrivacy)

    const validateInput = useCallback(
      (input: ReviewConfigInput) => {
        setErrors([])
        try {
          validate(input)
        } catch (e) {
          if (e instanceof ValidationError) {
            setErrors([e.message])
          }
        }
      },
      [validate],
    )

    useEffect(() => {
      validateInput({ reviewers, approvers, viewers })
    }, [reviewers, approvers, viewers, validateInput])

    const handleSubmit = (addMembers: boolean = false) => {
      if (errors.length > 0 || !title) return

      const config = {
        title,
        ...getParticipantsBuckets({ reviewers, approvers, viewers }),
        html_description: htmlMessage,
        duration: -dayjs().diff(endsOn, 'day') + 1,
        add_members: addMembers,
        layer_id: privacy === ReviewPrivacy.PRIVATE ? ourUuid() : undefined,
      }
      onConfirm(config)
      closeModal()
    }

    const setReviewerValues = (values: Value[]) => canAddReviewers && setReviewers(dehydrateValues(values, rootStore))
    const setApproverValues = (values: Value[]) => canAddReviewers && setApprovers(dehydrateValues(values, rootStore))
    const setViewerValues = (values: Value[]) => setViewers(dehydrateValues(values, rootStore))

    return (
      <ReviewConfigContext.Provider
        value={{
          reviewers,
          approvers,
          viewers,
          setReviewerValues,
          setApproverValues,
          setViewerValues,
          onSubmit: handleSubmit,
          htmlMessage,
          setHtmlMessage,
          endsOn,
          setEndsOn,
          reviewerRoles,
          setReviewerRoles,
          errors,
          title,
          privacy,
          setPrivacy,
          canAddReviewers,
          canChangePrivacy,
        }}
      >
        {children}
      </ReviewConfigContext.Provider>
    )
  },
)

export const useReviewConfig = () => {
  const context = useContext(ReviewConfigContext)
  if (context === undefined) {
    throw new Error('useReviewConfig must be within Provider')
  }

  return context
}
